import React, { useEffect, useState } from 'react';
import useAutomations from '../hooks/automationhooks/useAutomations';
import { useParams } from 'react-router-dom';
import { Box, Button, InputLabel, TextField, Typography } from '@mui/material';

export default function InforUTokenCreationPage() {
    const { brandId } = useParams();
    const {
        isLoading,
        userData,
        handleGetInforUToken,
        handleGetUserData,
        handleCreateInforUToken,
    } = useAutomations();

    const [response, setResponse] = useState(null);
    const [apiToken, setApiToken] = useState('');
    const [inforuUser, setUser] = useState('');
    const [isSubmitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const userId =
        userData?.data?.userId ??
        userData?.data?.UserId ??
        userData?.data?.Data?.userId ??
        userData?.data?.Data?.UserId ??
        userData?.userId ??
        userData?.UserId ??
        null;

    useEffect(() => {
        const init = async () => {
            setSubmitError('');
            try {
                // Load user data first so submit can work reliably.
                await handleGetUserData();

                if (brandId) {
                    const res = await handleGetInforUToken(brandId);
                    setResponse(res);
                } else {
                    setSubmitError('Missing brand id in the URL.');
                }
            } catch (e) {
                console.warn('Token fetch failed', e);
                setSubmitError('Failed to load initial data. Check console/network for details.');
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandId]);

    const handleSubmit = async () => {
        setSubmitError('');
        setSubmitSuccess(false);

        if (!brandId) {
            setSubmitError('Missing brand id in the URL.');
            return;
        }
        if (!userId) {
            setSubmitError('User data is not loaded (missing userId). Please refresh the page.');
            return;
        }
        if (!apiToken.trim() || !inforuUser.trim()) {
            setSubmitError('Please fill InforU API Token and InforU Username.');
            return;
        }

        setSubmitting(true);
        try {
            const newToken = await handleCreateInforUToken(
                brandId,
                userId,
                inforuUser.trim(),
                apiToken.trim()
            );
            // Keep response shape consistent with handleGetInforUToken (full axios response).
            setResponse(newToken);
            setSubmitSuccess(true);
        } catch (err) {
            console.error('Error creating token:', err);
            setSubmitError('Create token failed. Check console/network for the API error.');
        } finally {
            setSubmitting(false);
        }
    };

    const formValid =
        apiToken.trim() !== '' &&
        inforuUser.trim() !== '' &&
        Boolean(brandId) &&
        Boolean(userId);

    return (
        <>
            {isLoading && <div>Loading...</div>}

            {userData == null && !isLoading && (
                <div>Loading user data...</div>
            )}

            {!isLoading && response?.data ? (
                <div>Token already exists for this brand</div>
            ) : (
                <>
                    <Box>
                        <Box sx={{ mb: 2, mt: 2, ml: 2 }}>
                            <Typography color="companyBlue" variant="h6" gutterBottom>
                                InforU Token Saver
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        component="form"
                        noValidate
                        autoComplete="off"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }}
                        sx={{
                            bgcolor: 'background.paper',
                            p: 3,
                            borderRadius: 1,
                            mb: 2,
                            height: '100%',
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <InputLabel>InforU API Token</InputLabel>
                                <TextField
                                    name="apiToken"
                                    value={apiToken}
                                    onChange={(e) => setApiToken(e.target.value)}
                                    fullWidth
                                />
                            </Box>

                            <Box>
                                <InputLabel>InforU Username</InputLabel>
                                <TextField
                                    name="inforuUser"
                                    value={inforuUser}
                                    onChange={(e) => setUser(e.target.value)}
                                    fullWidth
                                />
                            </Box>

                            <Button
                                color="companyBlue"
                                aria-label="submit"
                                variant="contained"
                                disabled={!formValid || isSubmitting}
                                sx={{
                                    mt: 2,
                                    borderRadius: '50px',
                                    height: '40px',
                                    textTransform: 'none',
                                }}
                                onClick={handleSubmit}
                            >
                                <Typography color="text.primary">
                                    {isSubmitting ? 'Submitting…' : 'Submit'}
                                </Typography>
                            </Button>

                            {submitError ? (
                                <Typography sx={{ mt: 1 }} color="error" variant="body2">
                                    {submitError}
                                </Typography>
                            ) : null}

                            {submitSuccess ? (
                                <Typography sx={{ mt: 1 }} color="success.main" variant="body2">
                                    Token saved successfully.
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>
                </>
            )}
        </>
    );
}
